import React from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import { Inline } from "components/layout/reusableLayouts";

/**
 * The card actions, defined once. Every route picks which of these it offers;
 * none of them restyles one.
 */

interface CardActionProps {
  onClick: () => void;
  disabled?: boolean;
}

/** The row the buttons sit in. Wraps, because admin offers up to seven. */
export const CardActionBar: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Inline gap="var(--space-3)" justify="center" align="center">
    {children}
  </Inline>
);

export const EditCardButton: React.FC<CardActionProps> = ({
  onClick,
  disabled,
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="primary-filled"
      size="small"
      onClick={onClick}
      disabled={disabled}
    >
      {t("Edit")}
    </Button>
  );
};

export const CancelCardButton: React.FC<CardActionProps> = ({
  onClick,
  disabled,
}) => {
  const { t } = useTranslation();
  // Not "Cancel": the drawer's own exit is a button too, and this one kills the
  // card. Same words as the confirm it opens.
  return (
    <Button
      variant="primary-outlined"
      size="small"
      onClick={onClick}
      disabled={disabled}
    >
      {t("Cancel Card")}
    </Button>
  );
};

export const FreezeCardButton: React.FC<
  CardActionProps & { frozen: boolean }
> = ({ onClick, disabled, frozen }) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="primary-outlined"
      size="small"
      onClick={onClick}
      disabled={disabled}
    >
      {frozen ? t("Unfreeze") : t("Freeze")}
    </Button>
  );
};

export const ActivateCardButton: React.FC<CardActionProps> = ({
  onClick,
  disabled,
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="primary-filled"
      size="small"
      onClick={onClick}
      disabled={disabled}
    >
      {t("Activate Card")}
    </Button>
  );
};

export const ApproveCardButton: React.FC<CardActionProps> = ({
  onClick,
  disabled,
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="secondary-filled"
      size="small"
      onClick={onClick}
      disabled={disabled}
    >
      {t("Approve")}
    </Button>
  );
};

export const DenyCardButton: React.FC<CardActionProps> = ({
  onClick,
  disabled,
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="danger-outlined"
      size="small"
      onClick={onClick}
      disabled={disabled}
    >
      {t("Deny")}
    </Button>
  );
};
